import { motion } from 'framer-motion';
import type { Client } from '../data/clients';

type Props = {
  client: Client;
  index?: number;
};

function LogoMark({ client }: { client: Client }) {
  if (client.logo) {
    return (
      <img
        src={client.logo}
        alt={`${client.name} logo`}
        className="h-12 w-auto max-w-[180px] object-contain"
        loading="lazy"
      />
    );
  }
  
  const initials = client.name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/25 via-teal-500/15 to-ink-900 font-display text-lg font-semibold text-white/80">
      {initials || client.name[0]?.toUpperCase()}
    </div>
  );
}

export default function ClientCard({ client, index = 0 }: Props) {
  const Wrapper = client.url ? 'a' : 'div';
  const wrapperProps = client.url
    ? {
        href: client.url,
        target: '_blank' as const,
        rel: 'noreferrer',
      }
    : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, delay: index * 0.04 }}
    >
      <Wrapper
        {...wrapperProps}
        className={`group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-card transition hover:border-white/20 hover:bg-white/[0.05] ${
          client.url ? 'cursor-pointer' : ''
        }`}
      >
        <LogoMark client={client} />
        <h3 className="mt-5 font-display text-lg font-semibold text-white">
          {client.name}
        </h3>
        <p className="mt-1 text-xs font-medium uppercase tracking-[0.16em] text-accent-400">
          {client.industry}
        </p>
        <p className="mt-2 text-sm text-white/55">{client.region}</p>
        {client.url && (
          <p className="mt-auto pt-5 text-xs font-medium text-accent-400/80 opacity-0 transition group-hover:opacity-100">
            Visit site →
          </p>
        )}
      </Wrapper>
    </motion.div>
  );
}
